import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

function callable(name, options = {}) {
  if (!functions) throw new Error('Firebase Functions are not configured.');
  return httpsCallable(functions, name, options);
}

const ORGANIZATION_STORAGE_KEY = 'nudge.organization';

function withOrganization(payload = {}) {
  const organizationId = String(localStorage.getItem(ORGANIZATION_STORAGE_KEY) || '').trim();
  return organizationId ? { ...payload, organizationId } : payload;
}

export async function ensureNudgeAccount() {
  const response = await callable('nudgeEnsureAccount')(withOrganization({}));
  return response.data;
}

export async function getNudgeAppState() {
  const response = await callable('nudgeGetAppState')(withOrganization({}));
  return response.data;
}

export async function adminDeleteAccount(payload = {}) {
  const response = await callable('nudgeAdminDeleteAccount')(payload);
  return response.data;
}

export async function saveNudgeSettings({ settings, mailboxes, activate = false, workspaceId = '' }) {
  const response = await callable('nudgeSaveSettings')(withOrganization({ settings, mailboxes, activate, workspaceId }));
  return response.data;
}

export async function setNudgeStatus(active) {
  const response = await callable('nudgeSetStatus')(withOrganization({ active }));
  return response.data;
}

export async function scanNudgeMailbox({ mailboxId = '', maxThreads = 25, historical = false, lookbackDays = null } = {}) {
  const response = await callable('nudgeScanMailbox', { timeout: historical ? 540000 : 240000 })(withOrganization({
    mailboxId,
    maxThreads,
    historical,
    ...(lookbackDays ? { lookbackDays } : {}),
  }));
  return response.data;
}

export async function draftNextFollowUp({ threadId = '', force = false } = {}) {
  const response = await callable('nudgeDraftFollowUp')(withOrganization({ threadId, force }));
  return response.data;
}

export async function syncWatchedThreads({ maxThreads = 50, workspaceId = '' } = {}) {
  const response = await callable('nudgeSyncWatchedThreads')(withOrganization({ maxThreads, workspaceId }));
  return response.data;
}

export async function listOpportunities({ limit = 100, includeClosed = false, workspaceId = '' } = {}) {
  const response = await callable('nudgeListOpportunities')(withOrganization({ limit, includeClosed, workspaceId }));
  return response.data;
}

// Backward-compatible alias for older HomePage/OpportunityReviewTable imports.
export async function listNudgeOpportunities(options = {}) {
  return listOpportunities(options);
}

export async function updateOpportunity({ threadId, action, reason = '' }) {
  const response = await callable('nudgeUpdateOpportunity')(withOrganization({ threadId, action, reason }));
  return response.data;
}

// Backward-compatible alias for older HomePage/OpportunityReviewTable imports.
export async function updateNudgeOpportunity(payload = {}) {
  return updateOpportunity(payload);
}


export async function disconnectNudgeMailbox({ mailboxId = '', id = '', provider = '', email = '' } = {}) {
  const response = await callable('nudgeDisconnectMailbox')(withOrganization({ mailboxId, id, provider, email }));
  return response.data;
}

export async function createNudgeWorkspace({ name, copyFromWorkspaceId = '' }) {
  const response = await callable('nudgeCreateWorkspace')(withOrganization({ name, copyFromWorkspaceId }));
  return response.data;
}

export async function renameNudgeWorkspace({ workspaceId, name }) {
  const response = await callable('nudgeRenameWorkspace')(withOrganization({ workspaceId, name }));
  return response.data;
}

export async function deleteNudgeWorkspace({ workspaceId }) {
  const response = await callable('nudgeDeleteWorkspace')(withOrganization({ workspaceId }));
  return response.data;
}

export async function assignMailboxWorkspace({ mailboxId, workspaceId }) {
  const response = await callable('nudgeAssignMailboxWorkspace')(withOrganization({ mailboxId, workspaceId }));
  return response.data;
}

export async function saveMailboxNudgeSettings({ mailboxId, useWorkspaceDefaults = true, settings = {} }) {
  const response = await callable('nudgeSaveMailboxSettings')(withOrganization({ mailboxId, useWorkspaceDefaults, settings }));
  return response.data;
}

export async function saveEnrichmentConnection({ workspaceId, provider, apiKey }) {
  const response = await callable('nudgeSaveEnrichmentConnection')(withOrganization({ workspaceId, provider, apiKey }));
  return response.data;
}

export async function testEnrichmentConnection({ workspaceId, provider }) {
  const response = await callable('nudgeTestEnrichmentConnection')(withOrganization({ workspaceId, provider }));
  return response.data;
}

export async function disconnectEnrichment({ workspaceId, provider }) {
  const response = await callable('nudgeDisconnectEnrichmentConnection')(withOrganization({ workspaceId, provider }));
  return response.data;
}

export async function saveResearchSettings({ workspaceId, settings }) {
  const response = await callable('nudgeSaveResearchSettings')(withOrganization({ workspaceId, settings }));
  return response.data;
}

// Backward-compatible Cleanlist wrappers.
export async function saveCleanlistConnection({ workspaceId, apiKey }) {
  return saveEnrichmentConnection({ workspaceId, provider: 'cleanlist', apiKey });
}

export async function testCleanlistConnection({ workspaceId }) {
  return testEnrichmentConnection({ workspaceId, provider: 'cleanlist' });
}

export async function disconnectCleanlist({ workspaceId }) {
  return disconnectEnrichment({ workspaceId, provider: 'cleanlist' });
}

export async function researchAndDraft({ threadId, force = true }) {
  const response = await callable('nudgeResearchAndDraft', { timeout: 540000 })(withOrganization({ threadId, force }));
  return response.data;
}

export async function getResearchRun({ threadId = '', runId = '' } = {}) {
  const response = await callable('nudgeGetResearchRun')(withOrganization({ threadId, runId }));
  return response.data;
}

export async function getReferralSummary() {
  const response = await callable('nudgeGetReferralSummary')(withOrganization({}));
  return response.data;
}

export async function applyReferralCode({ code, source = 'manual' }) {
  const response = await callable('nudgeApplyReferralCode')(withOrganization({ code, source }));
  return response.data;
}

export async function claimActivationBonus() {
  const response = await callable('nudgeClaimActivationBonus')(withOrganization({}));
  return response.data;
}


export async function inviteOrganizationMember({ email, role = 'workspace_manager', workspaceIds = [] }) {
  const response = await callable('nudgeInviteOrganizationMember')(withOrganization({ email, role, workspaceIds }));
  return response.data;
}

export async function updateOrganizationMember({ memberId, role, workspaceIds = [] }) {
  const response = await callable('nudgeUpdateOrganizationMember')(withOrganization({ memberId, role, workspaceIds }));
  return response.data;
}

export async function removeOrganizationMember({ memberId }) {
  const response = await callable('nudgeRemoveOrganizationMember')(withOrganization({ memberId }));
  return response.data;
}

export async function resendOrganizationInvite({ memberId }) {
  const response = await callable('nudgeResendOrganizationInvite')(withOrganization({ memberId }));
  return response.data;
}

export async function runHistoricalOpportunityScan({ mailboxId = '', lookbackDays = 90, maxThreads = 75 } = {}) {
  return scanNudgeMailbox({ mailboxId, maxThreads, historical: true, lookbackDays });
}

// AppSumo licensing. The callable automatically targets the Firebase project for the current domain.
export async function claimAppSumoLicense({ token }) {
  const response = await callable('nudgeClaimAppSumoLicense')({ token });
  return response.data;
}

export async function adminLookupAppSumoLicense({ licenseKey }) {
  const response = await callable('nudgeAdminLookupAppSumoLicense')({ licenseKey });
  return response.data;
}

export async function adminApplyAppSumoLicense({ accountId, licenseKey, forceReassign = false }) {
  const response = await callable('nudgeAdminApplyAppSumoLicense')({
    accountId,
    licenseKey,
    forceReassign,
  });
  return response.data;
}

// Admin callables. Kept here so AdminPage does not break when launch/referral patches replace this service file.
export async function adminListAccounts({ search = '', limit = 50 } = {}) {
  const response = await callable('nudgeAdminListAccounts')({ search, limit });
  return response.data;
}

export async function adminUpdateSubscription(payload = {}) {
  const response = await callable('nudgeAdminUpdateSubscription')(payload);
  return response.data;
}

export async function adminAdjustCredits(payload = {}) {
  const response = await callable('nudgeAdminAdjustCredits')(payload);
  return response.data;
}

export async function adminSetAccountStatus(payload = {}) {
  const response = await callable('nudgeAdminSetAccountStatus')(payload);
  return response.data;
}

export async function adminResetMailbox(payload = {}) {
  const response = await callable('nudgeAdminResetMailbox')(payload);
  return response.data;
}

export async function adminSetTesterMode(payload = {}) {
  const response = await callable('nudgeAdminSetTesterMode')(payload);
  return response.data;
}

export async function adminGetAutomationDiagnostics(payload = {}) {
  const response = await callable('nudgeAdminGetAutomationDiagnostics', { timeout: 240000 })(payload);
  return response.data;
}

export async function adminRunTesterCycle(payload = {}) {
  const response = await callable('nudgeAdminRunTesterCycle', { timeout: 540000 })(payload);
  return response.data;
}

export async function createCheckoutSession({
  planId = '',
  interval = 'annual',
  launchRef = '',
  couponCode = '',
} = {}) {
  const response = await callable('nudgeCreateCheckoutSession')(withOrganization({
    planId,
    interval,
    launchRef,
    couponCode,
  }));
  return response.data;
}

export async function createBillingPortalSession() {
  const response = await callable('nudgeCreateBillingPortalSession')(withOrganization({}));
  return response.data;
}

export async function claimProductHuntOffer({ ref = 'PRODUCTHUNT', source = 'url' } = {}) {
  const response = await callable('nudgeClaimProductHuntOffer')(withOrganization({ ref, source }));
  return response.data;
}
